import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class Subdivide extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.subdivide',
        group: 'Spatial Operations',
        title: 'Subdivide',
        description: `
      This component subdivided large features into smaller ones, using a quadgrid.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          helper:
            "Approximate length, in meters, of the square cell's side used for subdivision",
          min: 100,
          max: 10000000,
          default: 100000
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let query = ''
    let select = ''
    const geoColumn = preparedParams.geo
    const size = preparedParams.size
    const inputSource = connection.quoteName(preparedParams.source as string)
    const atLocation = connection.quoteName(
      envVars.analyticsToolboxDataset as string
    )
    const inputColumns =
      (schemas?.input?.source as TableSchema)
        ?.map((column) => `${column?.name}, `)
        ?.join('') || ''
    const outputColumns =
      (schemas?.input?.source as TableSchema)
        ?.filter((column) => column?.name !== geoColumn)
        ?.map((column) => `${column?.name}, `)
        ?.join('') || ''

    if (connection.type === Provider.BigQuery) {
      const resolution = `CAST(ROUND(LOG(40075017/${size}*COS(ST_Y(ST_CENTROID(${geoColumn}))*ACOS(-1)/180), 2)) AS INT64)`
      select = dedent`
        WITH __polyfill AS (
          SELECT ${inputColumns} ${atLocation}.QUADBIN_POLYFILL_MODE(
            ${geoColumn},
            ${resolution},
            'intersects'
          ) AS __cells
          FROM ${inputSource}
        )
        SELECT
          ${outputColumns}
          ST_INTERSECTION(${geoColumn}, ${atLocation}.QUADBIN_BOUNDARY(__cell)) AS ${geoColumn}
        FROM __polyfill p, p.__cells AS __cell`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.Snowflake) {
      const resolution = `ROUND(LOG(2, 40075017/${size}*COS(ST_Y(ST_CENTROID(${geoColumn}))*PI()/180)))`
      select = dedent`
        WITH __polyfill AS (
          SELECT ${inputColumns} ${atLocation}.QUADBIN_POLYFILL(
            ${geoColumn},
            ${resolution}
          ) AS __cells
          FROM ${inputSource}
        )
        SELECT
          ${outputColumns}
          ST_INTERSECTION(${geoColumn}, ${atLocation}.QUADBIN_BOUNDARY(__cell.value::BIGINT)) AS ${geoColumn}
        FROM __polyfill p, TABLE(FLATTEN(p.__cells)) AS __cell`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.Postgres) {
      const resolution = `ROUND(LOG(2, 40075017/${size}*COS(ST_Y(ST_CENTROID(${geoColumn}))*PI()/180)::NUMERIC))::INT`
      select = dedent`
        WITH __polyfill AS (
          SELECT ${inputColumns} ${atLocation}.QUADBIN_POLYFILL(
            ${geoColumn},
            ${resolution},
            'intersects'
          ) AS __cells
          FROM ${inputSource}
        )
        SELECT
          ${outputColumns}
          ST_INTERSECTION(${geoColumn}, ${atLocation}.QUADBIN_BOUNDARY(__cell)) AS ${geoColumn}
        FROM __polyfill p, UNNEST(p.__cells) AS __cell`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    } else if (connection.type === Provider.Redshift) {
      const resolution = `ROUND(LOG(40075017/${size}*COS(ST_Y(ST_CENTROID(${geoColumn}))*PI()/180))/LOG(2))::INT`
      select = dedent`
        SELECT
          ${outputColumns}
          ST_INTERSECTION(${geoColumn}, ${atLocation}.QUADBIN_BOUNDARY(__cell::BIGINT)) AS ${geoColumn}
        FROM ${outputTablename}_temp p, p.__cells AS __cell`
      const createTable = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
      query = dedent`
        CREATE TABLE ${outputTablename}_temp AS
          SELECT ${inputColumns} ${atLocation}.QUADBIN_POLYFILL(
            ${geoColumn},
            ${resolution}
          ) AS __cells
          FROM ${inputSource};
        ${createTable}
        DROP TABLE ${outputTablename}_temp;`
    } else if (connection.type === Provider.DatabricksRest) {
      const resolution = `ROUND(LOG(2, 40075017/${size}*COS(ST_Y(ST_CENTROID(${geoColumn}_processed))*PI()/180)))`
      select = dedent`
        WITH __polyfill AS (
          SELECT  /*+ REPARTITION(40) */
            ${inputColumns}
            EXPLODE(QUADBIN_POLYFILL(
              ST_ASTEXT(${geoColumn}_processed),
              ${resolution}
            )) AS __cell,
            ${geoColumn}_processed
          FROM (SELECT *, ${connection.processInputGeogColumn(
            geoColumn as string
          )} AS ${geoColumn}_processed FROM ${inputSource})
        )
        SELECT /*+ REPARTITION(40) */
          ${outputColumns}
          ${connection.processOutputGeogColumn(
            `ST_INTERSECTION(${geoColumn}_processed, ST_GEOMFROMTEXT(QUADBIN_BOUNDARY(__cell)))`
          )} AS ${geoColumn}
        FROM __polyfill p`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.2.0'
      case Provider.BigQuery:
        return '1.1.0'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return columns.source
  }
}

export { Subdivide }
